import React, { useMemo } from 'react';
import { Ban, Eye } from 'lucide-react';
import { formatCategory } from '../../data/validationData';
import { ValidationProject } from '../../types/validation';

interface NonEligibleViewProps {
  projects: ValidationProject[];
  onViewProject: (projectId: string) => void;
}

const NonEligibleView: React.FC<NonEligibleViewProps> = ({ projects, onViewProject }) => {
  const nonEligibleProjects = useMemo(
    () => projects.filter(p => p.status === 'non-eligible'),
    [projects],
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Non-Eligible Projects</h2>
        <p className="text-gray-600 mt-1">
          Projects that have been marked as non-eligible during the validation process.
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex items-center gap-4">
        <div className="p-3 bg-gray-100 rounded-full">
          <Ban className="w-6 h-6 text-gray-500" />
        </div>
        <div>
          <div className="text-3xl font-bold text-gray-900">{nonEligibleProjects.length}</div>
          <div className="text-sm text-gray-500">Non-Eligible Projects</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Non-Eligible Projects ({nonEligibleProjects.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Non-eligible projects">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location/Basin</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {nonEligibleProjects.map(project => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.externalId || project.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {project.type === 'new' ? 'New' : 'Historic'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCategory(project.category)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{project.details.basin}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onViewProject(project.id)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1"
                      aria-label={`View ${project.name}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {nonEligibleProjects.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No non-eligible projects
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NonEligibleView;
